import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { supabase } from '../supabase';
import useBreakpoint from '../hooks/useBreakpoint';
import { sheet, toastMobile, tap, pageMobile } from '../mobileStyles';

const COLOR_PALETTE = ['#6B1C2A', '#C97B32', '#4A1219', '#8B6914', '#3E2A1A', '#2A3E1A', '#C9A96E', '#4A7C59', '#7A8B8B', '#3E2118'];
const TIPO_OPTIONS = [
  { value: 'diseño', label: 'Diseño' },
  { value: 'obra',   label: 'Obra' },
];

const EMPTY_CAT = { nombre: '', tipo: 'obra', color: '#6B1C2A' };

function PencilIcon() {
  return (
    <svg width="11" height="11" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M13.5 3.5l3 3L7 16H4v-3L13.5 3.5z" />
    </svg>
  );
}

export default function Etiquetas() {
  const { isMobile, isCompact } = useBreakpoint();
  // Phones and tablets get ≥44px tap targets on small icon buttons and swatches.
  const touchUI = isCompact;
  const [categorias, setCategorias] = useState([]);
  const [loadingCat, setLoadingCat] = useState(true);
  const [toast, setToast] = useState(null);
  const [tipoFilter, setTipoFilter] = useState('all'); // 'all' | 'diseño' | 'obra'

  const [catModal, setCatModal] = useState(null);
  const [catSaving, setCatSaving] = useState(false);
  const [deletingCatId, setDeletingCatId] = useState(null);

  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [roleModal, setRoleModal] = useState(null);
  const [roleSaving, setRoleSaving] = useState(false);
  const [deletingRoleId, setDeletingRoleId] = useState(null);

  const [origenes, setOrigenes] = useState([]);
  const [loadingOrigenes, setLoadingOrigenes] = useState(true);
  const [origenModal, setOrigenModal] = useState(null);
  const [origenSaving, setOrigenSaving] = useState(false);
  const [deletingOrigenId, setDeletingOrigenId] = useState(null);

  const [especialidades, setEspecialidades] = useState([]);
  const [loadingEspecialidades, setLoadingEspecialidades] = useState(true);
  const [especialidadModal, setEspecialidadModal] = useState(null);
  const [especialidadSaving, setEspecialidadSaving] = useState(false);
  const [deletingEspecialidadId, setDeletingEspecialidadId] = useState(null);

  function showToast(message, type = 'success') {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  }

  const fetchCategorias = useCallback(async () => {
    setLoadingCat(true);
    const { data, error } = await supabase.from('categorias').select('*').order('nombre');
    if (error) { showToast('Error cargando categorías: ' + error.message, 'error'); setLoadingCat(false); return; }
    setCategorias(data || []);
    setLoadingCat(false);
  }, []);

  useEffect(() => { fetchCategorias(); }, [fetchCategorias]);

  const visibleCategorias = useMemo(() => {
    if (tipoFilter === 'all') return categorias;
    return categorias.filter(c => (c.tipo || 'obra') === tipoFilter);
  }, [categorias, tipoFilter]);

  function openNewCategoria() {
    setCatModal({ draft: { ...EMPTY_CAT, tipo: tipoFilter === 'diseño' ? 'diseño' : 'obra' } });
  }
  function openEditCategoria(cat) {
    setCatModal({
      editingId: cat.id,
      draft: {
        nombre: cat.nombre || '',
        tipo: cat.tipo || 'obra',
        color: cat.color || '#6B1C2A',
      },
    });
  }
  function closeCatModal() { setCatModal(null); }

  async function saveCategoria(e) {
    e.preventDefault();
    if (!catModal || catSaving) return;
    const draft = catModal.draft;
    if (!draft.nombre.trim()) return;
    setCatSaving(true);
    const payload = { nombre: draft.nombre.trim(), tipo: draft.tipo || 'obra', color: draft.color || '#6B1C2A' };
    const resp = catModal.editingId
      ? await supabase.from('categorias').update(payload).eq('id', catModal.editingId).select()
      : await supabase.from('categorias').insert([payload]).select();
    setCatSaving(false);
    if (resp.error) { showToast('Error: ' + resp.error.message, 'error'); return; }
    if (!resp.data || resp.data.length === 0) {
      showToast(catModal.editingId
        ? 'No se actualizó (posible bloqueo RLS en categorias)'
        : 'No se insertó (posible bloqueo RLS en categorias)', 'error');
      return;
    }
    await fetchCategorias();
    const wasEditing = !!catModal.editingId;
    setCatModal(null);
    showToast(wasEditing ? 'Categoría actualizada' : 'Categoría creada');
  }

  async function deleteCategoria(cat) {
    if (!cat || deletingCatId) return;
    if (!window.confirm(`¿Eliminar la categoría "${cat.nombre}"? Las tareas que la usan conservarán el nombre pero perderán el color.`)) return;
    setDeletingCatId(cat.id);
    const { data, error } = await supabase.from('categorias').delete().eq('id', cat.id).select();
    setDeletingCatId(null);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) { showToast('No se eliminó (posible bloqueo RLS en categorias)', 'error'); return; }
    await fetchCategorias();
    showToast('Categoría eliminada');
  }

  const fetchRoles = useCallback(async () => {
    setLoadingRoles(true);
    const { data, error } = await supabase.from('roles').select('*').order('nombre');
    if (error) {
      if (/relation .* does not exist/i.test(error.message || '')) {
        showToast('Falta la tabla roles — ejecuta supabase-roles-migration.sql', 'error');
      } else {
        showToast('Error cargando roles: ' + error.message, 'error');
      }
      setLoadingRoles(false);
      return;
    }
    setRoles(data || []);
    setLoadingRoles(false);
  }, []);

  useEffect(() => { fetchRoles(); }, [fetchRoles]);

  function openNewRol() { setRoleModal({ draft: { nombre: '' } }); }
  function openEditRol(r) { setRoleModal({ editingId: r.id, draft: { nombre: r.nombre || '' } }); }
  function closeRoleModal() { setRoleModal(null); }

  async function saveRol(e) {
    e.preventDefault();
    if (!roleModal || roleSaving) return;
    const nombre = roleModal.draft.nombre.trim();
    if (!nombre) return;
    setRoleSaving(true);
    const resp = roleModal.editingId
      ? await supabase.from('roles').update({ nombre }).eq('id', roleModal.editingId).select()
      : await supabase.from('roles').insert([{ nombre }]).select();
    setRoleSaving(false);
    if (resp.error) { showToast('Error: ' + resp.error.message, 'error'); return; }
    if (!resp.data || resp.data.length === 0) {
      showToast(roleModal.editingId
        ? 'No se actualizó (posible bloqueo RLS en roles)'
        : 'No se insertó (posible bloqueo RLS en roles)', 'error');
      return;
    }
    await fetchRoles();
    const wasEditing = !!roleModal.editingId;
    setRoleModal(null);
    showToast(wasEditing ? 'Cargo actualizado' : 'Cargo agregado');
  }

  async function deleteRol(r) {
    if (!r || deletingRoleId) return;
    if (!window.confirm(`¿Eliminar el cargo "${r.nombre}"? Las personas que lo tengan asignado conservarán el texto.`)) return;
    setDeletingRoleId(r.id);
    const { data, error } = await supabase.from('roles').delete().eq('id', r.id).select();
    setDeletingRoleId(null);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) { showToast('No se eliminó (posible bloqueo RLS en roles)', 'error'); return; }
    await fetchRoles();
    showToast('Cargo eliminado');
  }

  const fetchOrigenes = useCallback(async () => {
    setLoadingOrigenes(true);
    const { data, error } = await supabase.from('origenes_contacto').select('*').order('nombre');
    if (error) {
      if (/relation .* does not exist/i.test(error.message || '')) {
        showToast('Falta la tabla origenes_contacto — ejecuta supabase-origenes-contacto-migration.sql', 'error');
      } else {
        showToast('Error cargando orígenes: ' + error.message, 'error');
      }
      setLoadingOrigenes(false);
      return;
    }
    setOrigenes(data || []);
    setLoadingOrigenes(false);
  }, []);

  useEffect(() => { fetchOrigenes(); }, [fetchOrigenes]);

  function openNewOrigen() { setOrigenModal({ draft: { nombre: '' } }); }
  function openEditOrigen(o) { setOrigenModal({ editingId: o.id, draft: { nombre: o.nombre || '' } }); }
  function closeOrigenModal() { setOrigenModal(null); }

  async function saveOrigen(e) {
    e.preventDefault();
    if (!origenModal || origenSaving) return;
    const nombre = origenModal.draft.nombre.trim();
    if (!nombre) return;
    setOrigenSaving(true);
    const resp = origenModal.editingId
      ? await supabase.from('origenes_contacto').update({ nombre }).eq('id', origenModal.editingId).select()
      : await supabase.from('origenes_contacto').insert([{ nombre }]).select();
    setOrigenSaving(false);
    if (resp.error) { showToast('Error: ' + resp.error.message, 'error'); return; }
    if (!resp.data || resp.data.length === 0) {
      showToast(origenModal.editingId
        ? 'No se actualizó (posible bloqueo RLS en origenes_contacto)'
        : 'No se insertó (posible bloqueo RLS en origenes_contacto)', 'error');
      return;
    }
    await fetchOrigenes();
    const wasEditing = !!origenModal.editingId;
    setOrigenModal(null);
    showToast(wasEditing ? 'Origen actualizado' : 'Origen agregado');
  }

  async function deleteOrigen(o) {
    if (!o || deletingOrigenId) return;
    if (!window.confirm(`¿Eliminar el origen "${o.nombre}"? Los clientes que lo tengan asignado conservarán el texto.`)) return;
    setDeletingOrigenId(o.id);
    const { data, error } = await supabase.from('origenes_contacto').delete().eq('id', o.id).select();
    setDeletingOrigenId(null);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) { showToast('No se eliminó (posible bloqueo RLS en origenes_contacto)', 'error'); return; }
    await fetchOrigenes();
    showToast('Origen eliminado');
  }

  const fetchEspecialidades = useCallback(async () => {
    setLoadingEspecialidades(true);
    const { data, error } = await supabase.from('especialidades').select('*').order('nombre');
    if (error) {
      if (/relation .* does not exist/i.test(error.message || '')) {
        showToast('Falta la tabla especialidades — ejecuta supabase-especialidades-migration.sql', 'error');
      } else {
        showToast('Error cargando especialidades: ' + error.message, 'error');
      }
      setLoadingEspecialidades(false);
      return;
    }
    setEspecialidades(data || []);
    setLoadingEspecialidades(false);
  }, []);

  useEffect(() => { fetchEspecialidades(); }, [fetchEspecialidades]);

  function openNewEspecialidad() { setEspecialidadModal({ draft: { nombre: '' } }); }
  function openEditEspecialidad(e) { setEspecialidadModal({ editingId: e.id, draft: { nombre: e.nombre || '' } }); }
  function closeEspecialidadModal() { setEspecialidadModal(null); }

  async function saveEspecialidad(e) {
    e.preventDefault();
    if (!especialidadModal || especialidadSaving) return;
    const nombre = especialidadModal.draft.nombre.trim();
    if (!nombre) return;
    setEspecialidadSaving(true);
    const resp = especialidadModal.editingId
      ? await supabase.from('especialidades').update({ nombre }).eq('id', especialidadModal.editingId).select()
      : await supabase.from('especialidades').insert([{ nombre }]).select();
    setEspecialidadSaving(false);
    if (resp.error) { showToast('Error: ' + resp.error.message, 'error'); return; }
    if (!resp.data || resp.data.length === 0) {
      showToast(especialidadModal.editingId
        ? 'No se actualizó (posible bloqueo RLS en especialidades)'
        : 'No se insertó (posible bloqueo RLS en especialidades)', 'error');
      return;
    }
    await fetchEspecialidades();
    const wasEditing = !!especialidadModal.editingId;
    setEspecialidadModal(null);
    showToast(wasEditing ? 'Especialidad actualizada' : 'Especialidad agregada');
  }

  async function deleteEspecialidad(e) {
    if (!e || deletingEspecialidadId) return;
    if (!window.confirm(`¿Eliminar la especialidad "${e.nombre}"? Los proveedores que la tengan asignada conservarán el texto.`)) return;
    setDeletingEspecialidadId(e.id);
    const { data, error } = await supabase.from('especialidades').delete().eq('id', e.id).select();
    setDeletingEspecialidadId(null);
    if (error) { showToast('Error: ' + error.message, 'error'); return; }
    if (!data || data.length === 0) { showToast('No se eliminó (posible bloqueo RLS en especialidades)', 'error'); return; }
    await fetchEspecialidades();
    showToast('Especialidad eliminada');
  }

  return (
    <div style={isMobile ? { ...s.container, ...pageMobile } : s.container}>
      <header style={s.header}>
        <div>
          <h2 style={s.title}>Etiquetas</h2>
          <p style={isMobile ? { ...s.subtitle, ...s.textMobile } : s.subtitle}>Categorías de tareas usadas en el Gantt</p>
        </div>
      </header>

      <section style={isMobile ? { ...s.card, ...s.cardMobile } : s.card}>
        <div style={isMobile ? { ...s.cardHeader, ...s.cardHeaderMobile } : s.cardHeader}>
          <div>
            <h3 style={s.cardTitle}>Categorías</h3>
            <p style={isMobile ? { ...s.cardSub, ...s.textMobile } : s.cardSub}>Se usan al crear o editar tareas en el Gantt</p>
          </div>
          <div style={isMobile ? s.catControlsMobile : { display: 'flex', gap: 10, alignItems: 'center' }}>
            <div style={isMobile ? { ...s.filterTabs, ...s.filterTabsMobile } : s.filterTabs}>
              {[{ v:'all', l:'Todas' }, { v:'diseño', l:'Diseño' }, { v:'obra', l:'Obra' }].map(t => (
                <button
                  key={t.v}
                  type="button"
                  onClick={() => setTipoFilter(t.v)}
                  style={isMobile ? { ...s.filterTab, ...s.filterTabMobile, ...(tipoFilter === t.v ? s.filterTabActive : {}) } : { ...s.filterTab, ...(tipoFilter === t.v ? s.filterTabActive : {}) }}
                >
                  {t.l}
                </button>
              ))}
            </div>
            <button style={isMobile ? { ...s.primaryBtn, ...tap } : s.primaryBtn} onClick={openNewCategoria}>+ Nueva categoría</button>
          </div>
        </div>

        {loadingCat ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>Cargando categorías…</div>
        ) : visibleCategorias.length === 0 ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>No hay categorías todavía.</div>
        ) : (
          <div style={isMobile ? { ...s.pillGrid, ...s.pillGridMobile } : s.pillGrid}>
            {visibleCategorias.map(c => (
              <div
                key={c.id}
                style={isMobile ? { ...s.pill, ...s.pillMobile, borderColor: `${c.color}55`, background: `${c.color}10` } : { ...s.pill, borderColor: `${c.color}55`, background: `${c.color}10` }}
              >
                <span style={{ ...s.pillDot, background: c.color }} />
                <span style={isMobile ? s.pillNameMobile : s.pillName}>{c.nombre}</span>
                <span style={s.pillType}>{c.tipo === 'diseño' ? 'Diseño' : 'Obra'}</span>
                <button
                  type="button"
                  onClick={() => openEditCategoria(c)}
                  style={touchUI ? { ...s.pillEdit, ...s.pillBtnTouch } : s.pillEdit}
                  title="Editar categoría"
                  aria-label={`Editar ${c.nombre}`}
                >
                  <PencilIcon />
                </button>
                <button
                  type="button"
                  onClick={() => deleteCategoria(c)}
                  disabled={deletingCatId === c.id}
                  style={touchUI ? { ...s.pillDelete, ...s.pillBtnTouch, fontSize: 14 } : s.pillDelete}
                  title="Eliminar categoría"
                  aria-label={`Eliminar ${c.nombre}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={isMobile ? { ...s.card, ...s.cardMobile, marginTop: 20 } : { ...s.card, marginTop: 20 }}>
        <div style={isMobile ? { ...s.cardHeader, ...s.cardHeaderMobile } : s.cardHeader}>
          <div>
            <h3 style={s.cardTitle}>Cargos</h3>
            <p style={isMobile ? { ...s.cardSub, ...s.textMobile } : s.cardSub}>Cargo para miembros del equipo y especialidad para proveedores</p>
          </div>
          <button style={isMobile ? { ...s.primaryBtn, ...tap } : s.primaryBtn} onClick={openNewRol}>+ Nuevo cargo</button>
        </div>

        {loadingRoles ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>Cargando cargos…</div>
        ) : roles.length === 0 ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>No hay cargos todavía.</div>
        ) : (
          <div style={isMobile ? { ...s.pillGrid, ...s.pillGridMobile } : s.pillGrid}>
            {roles.map(r => (
              <div key={r.id} style={isMobile ? { ...s.rolePill, ...s.pillMobile } : s.rolePill}>
                <span style={isMobile ? s.pillNameMobile : s.pillName}>{r.nombre}</span>
                <button
                  type="button"
                  onClick={() => openEditRol(r)}
                  style={touchUI ? { ...s.pillEdit, ...s.pillBtnTouch } : s.pillEdit}
                  title="Editar cargo"
                  aria-label={`Editar ${r.nombre}`}
                >
                  <PencilIcon />
                </button>
                <button
                  type="button"
                  onClick={() => deleteRol(r)}
                  disabled={deletingRoleId === r.id}
                  style={touchUI ? { ...s.pillDelete, ...s.pillBtnTouch, fontSize: 14 } : s.pillDelete}
                  title="Eliminar cargo"
                  aria-label={`Eliminar ${r.nombre}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={isMobile ? { ...s.card, ...s.cardMobile, marginTop: 20 } : { ...s.card, marginTop: 20 }}>
        <div style={isMobile ? { ...s.cardHeader, ...s.cardHeaderMobile } : s.cardHeader}>
          <div>
            <h3 style={s.cardTitle}>Origen de contacto</h3>
            <p style={isMobile ? { ...s.cardSub, ...s.textMobile } : s.cardSub}>Cómo llegó el cliente al estudio</p>
          </div>
          <button style={isMobile ? { ...s.primaryBtn, ...tap } : s.primaryBtn} onClick={openNewOrigen}>+ Nuevo origen</button>
        </div>

        {loadingOrigenes ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>Cargando orígenes…</div>
        ) : origenes.length === 0 ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>No hay orígenes todavía.</div>
        ) : (
          <div style={isMobile ? { ...s.pillGrid, ...s.pillGridMobile } : s.pillGrid}>
            {origenes.map(o => (
              <div key={o.id} style={isMobile ? { ...s.rolePill, ...s.pillMobile } : s.rolePill}>
                <span style={isMobile ? s.pillNameMobile : s.pillName}>{o.nombre}</span>
                <button
                  type="button"
                  onClick={() => openEditOrigen(o)}
                  style={touchUI ? { ...s.pillEdit, ...s.pillBtnTouch } : s.pillEdit}
                  title="Editar origen"
                  aria-label={`Editar ${o.nombre}`}
                >
                  <PencilIcon />
                </button>
                <button
                  type="button"
                  onClick={() => deleteOrigen(o)}
                  disabled={deletingOrigenId === o.id}
                  style={touchUI ? { ...s.pillDelete, ...s.pillBtnTouch, fontSize: 14 } : s.pillDelete}
                  title="Eliminar origen"
                  aria-label={`Eliminar ${o.nombre}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section style={isMobile ? { ...s.card, ...s.cardMobile, marginTop: 20 } : { ...s.card, marginTop: 20 }}>
        <div style={isMobile ? { ...s.cardHeader, ...s.cardHeaderMobile } : s.cardHeader}>
          <div>
            <h3 style={s.cardTitle}>Especialidad</h3>
            <p style={isMobile ? { ...s.cardSub, ...s.textMobile } : s.cardSub}>Especialidades disponibles para proveedores</p>
          </div>
          <button style={isMobile ? { ...s.primaryBtn, ...tap } : s.primaryBtn} onClick={openNewEspecialidad}>+ Nueva especialidad</button>
        </div>

        {loadingEspecialidades ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>Cargando especialidades…</div>
        ) : especialidades.length === 0 ? (
          <div style={isMobile ? { ...s.empty, ...s.textMobile } : s.empty}>No hay especialidades todavía.</div>
        ) : (
          <div style={isMobile ? { ...s.pillGrid, ...s.pillGridMobile } : s.pillGrid}>
            {especialidades.map(esp => (
              <div key={esp.id} style={isMobile ? { ...s.rolePill, ...s.pillMobile } : s.rolePill}>
                <span style={isMobile ? s.pillNameMobile : s.pillName}>{esp.nombre}</span>
                <button
                  type="button"
                  onClick={() => openEditEspecialidad(esp)}
                  style={touchUI ? { ...s.pillEdit, ...s.pillBtnTouch } : s.pillEdit}
                  title="Editar especialidad"
                  aria-label={`Editar ${esp.nombre}`}
                >
                  <PencilIcon />
                </button>
                <button
                  type="button"
                  onClick={() => deleteEspecialidad(esp)}
                  disabled={deletingEspecialidadId === esp.id}
                  style={touchUI ? { ...s.pillDelete, ...s.pillBtnTouch, fontSize: 14 } : s.pillDelete}
                  title="Eliminar especialidad"
                  aria-label={`Eliminar ${esp.nombre}`}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      {especialidadModal && (
        <div style={isMobile ? { ...s.overlay, ...sheet.overlay } : s.overlay} onClick={closeEspecialidadModal}>
          <div style={isMobile ? { ...s.modal, ...sheet.panel } : s.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...s.modalHeaderMobile, ...sheet.header } : s.modalHeader}>
              <h3 style={s.modalTitle}>{especialidadModal.editingId ? 'Editar especialidad' : 'Nueva especialidad'}</h3>
              <button style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={closeEspecialidadModal}>✕</button>
            </div>
            <form onSubmit={saveEspecialidad} style={isMobile ? { ...s.form, ...s.formMobile } : s.form}>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Nombre de la especialidad</span>
                <input
                  style={s.input}
                  required
                  autoFocus
                  placeholder="Ej. Carpintería"
                  value={especialidadModal.draft.nombre}
                  onChange={e => setEspecialidadModal(m => ({ ...m, draft: { nombre: e.target.value } }))}
                />
              </label>
              <div style={isMobile ? { ...s.modalActions, ...s.modalActionsMobile, ...sheet.footer } : s.modalActions}>
                <button type="button" style={isMobile ? { ...s.cancelBtn, ...tap, flex: 1 } : s.cancelBtn} onClick={closeEspecialidadModal}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...s.primaryBtn, ...tap, flex: 1 } : s.primaryBtn} disabled={especialidadSaving}>
                  {especialidadSaving ? 'Guardando…' : (especialidadModal.editingId ? 'Guardar cambios' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {origenModal && (
        <div style={isMobile ? { ...s.overlay, ...sheet.overlay } : s.overlay} onClick={closeOrigenModal}>
          <div style={isMobile ? { ...s.modal, ...sheet.panel } : s.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...s.modalHeaderMobile, ...sheet.header } : s.modalHeader}>
              <h3 style={s.modalTitle}>{origenModal.editingId ? 'Editar origen' : 'Nuevo origen'}</h3>
              <button style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={closeOrigenModal}>✕</button>
            </div>
            <form onSubmit={saveOrigen} style={isMobile ? { ...s.form, ...s.formMobile } : s.form}>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Nombre del origen</span>
                <input
                  style={s.input}
                  required
                  autoFocus
                  placeholder="Ej. Instagram"
                  value={origenModal.draft.nombre}
                  onChange={e => setOrigenModal(m => ({ ...m, draft: { nombre: e.target.value } }))}
                />
              </label>
              <div style={isMobile ? { ...s.modalActions, ...s.modalActionsMobile, ...sheet.footer } : s.modalActions}>
                <button type="button" style={isMobile ? { ...s.cancelBtn, ...tap, flex: 1 } : s.cancelBtn} onClick={closeOrigenModal}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...s.primaryBtn, ...tap, flex: 1 } : s.primaryBtn} disabled={origenSaving}>
                  {origenSaving ? 'Guardando…' : (origenModal.editingId ? 'Guardar cambios' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {roleModal && (
        <div style={isMobile ? { ...s.overlay, ...sheet.overlay } : s.overlay} onClick={closeRoleModal}>
          <div style={isMobile ? { ...s.modal, ...sheet.panel } : s.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...s.modalHeaderMobile, ...sheet.header } : s.modalHeader}>
              <h3 style={s.modalTitle}>{roleModal.editingId ? 'Editar cargo' : 'Nuevo cargo'}</h3>
              <button style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={closeRoleModal}>✕</button>
            </div>
            <form onSubmit={saveRol} style={isMobile ? { ...s.form, ...s.formMobile } : s.form}>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Nombre del cargo</span>
                <input
                  style={s.input}
                  required
                  autoFocus
                  placeholder="Ej. Maestro de obra"
                  value={roleModal.draft.nombre}
                  onChange={e => setRoleModal(m => ({ ...m, draft: { nombre: e.target.value } }))}
                />
              </label>
              <div style={isMobile ? { ...s.modalActions, ...s.modalActionsMobile, ...sheet.footer } : s.modalActions}>
                <button type="button" style={isMobile ? { ...s.cancelBtn, ...tap, flex: 1 } : s.cancelBtn} onClick={closeRoleModal}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...s.primaryBtn, ...tap, flex: 1 } : s.primaryBtn} disabled={roleSaving}>
                  {roleSaving ? 'Guardando…' : (roleModal.editingId ? 'Guardar cambios' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {catModal && (
        <div style={isMobile ? { ...s.overlay, ...sheet.overlay } : s.overlay} onClick={closeCatModal}>
          <div style={isMobile ? { ...s.modal, ...sheet.panel } : s.modal} onClick={e => e.stopPropagation()}>
            <div style={isMobile ? { ...s.modalHeader, ...s.modalHeaderMobile, ...sheet.header } : s.modalHeader}>
              <h3 style={s.modalTitle}>{catModal.editingId ? 'Editar categoría' : 'Nueva categoría'}</h3>
              <button style={isMobile ? { ...s.closeBtn, ...sheet.closeBtn } : s.closeBtn} onClick={closeCatModal}>✕</button>
            </div>
            <form onSubmit={saveCategoria} style={isMobile ? { ...s.form, ...s.formMobile } : s.form}>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Nombre</span>
                <input
                  style={s.input}
                  autoFocus
                  required
                  value={catModal.draft.nombre}
                  onChange={e => setCatModal(m => ({ ...m, draft: { ...m.draft, nombre: e.target.value } }))}
                />
              </label>
              <label style={s.fieldGroup}>
                <span style={s.fieldLabel}>Tipo</span>
                <select
                  style={s.input}
                  value={catModal.draft.tipo}
                  onChange={e => setCatModal(m => ({ ...m, draft: { ...m.draft, tipo: e.target.value } }))}
                >
                  {TIPO_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
              <div style={s.fieldGroup}>
                <span style={s.fieldLabel}>Color</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {COLOR_PALETTE.map(c => {
                    const active = catModal.draft.color === c;
                    return (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCatModal(m => ({ ...m, draft: { ...m.draft, color: c } }))}
                        style={{
                          width: 30, height: 30, borderRadius: 8, background: c,
                          border: active ? '3px solid #2D2D2D' : '2px solid #fff',
                          cursor: 'pointer', boxShadow: '0 1px 4px rgba(0,0,0,0.15)',
                          ...(touchUI ? s.swatchTouch : {}),
                        }}
                        aria-label={`Color ${c}`}
                      />
                    );
                  })}
                </div>
              </div>
              <div style={isMobile ? { ...s.modalActions, ...s.modalActionsMobile, ...sheet.footer } : s.modalActions}>
                <button type="button" style={isMobile ? { ...s.cancelBtn, ...tap, flex: 1 } : s.cancelBtn} onClick={closeCatModal}>Cancelar</button>
                <button type="submit" style={isMobile ? { ...s.primaryBtn, ...tap, flex: 1 } : s.primaryBtn} disabled={catSaving}>
                  {catSaving ? 'Guardando…' : (catModal.editingId ? 'Guardar cambios' : 'Crear')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {toast && (
        <div style={{ position:'fixed', bottom:28, left:'50%', transform:'translateX(-50%)',
          background: toast.type === 'error' ? '#B83B3B' : '#4A7C59',
          color:'#fff', padding:'10px 24px', borderRadius:10, fontSize:13, fontWeight:600,
          fontFamily:"'DM Sans',sans-serif", zIndex:9999, boxShadow:'0 4px 20px rgba(0,0,0,0.2)',
          ...(isMobile ? toastMobile : {}) }}>
          {toast.message}
        </div>
      )}
    </div>
  );
}

const s = {
  container: { padding:'32px 36px', maxWidth:1400 },
  header: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 },
  title: { fontFamily:"'Cormorant Garamond',serif", fontSize:32, fontWeight:600, color:'var(--wine)', margin:0 },
  subtitle: { fontSize:13, color:'var(--text-secondary)', marginTop:4 },

  card: { background:'#fff', borderRadius:14, border:'1px solid var(--border)', padding:'20px 24px' },
  cardHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:16, gap:16, flexWrap:'wrap' },
  cardTitle: { fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:600, color:'var(--wine)', margin:0 },
  cardSub: { fontSize:12, color:'var(--text-light)', marginTop:2 },

  filterTabs: { display:'inline-flex', padding:3, background:'#FDFBF8', border:'1px solid var(--border)', borderRadius:8, gap:2 },
  filterTab: { padding:'6px 14px', border:'none', background:'transparent', fontSize:12, fontWeight:600, cursor:'pointer', color:'var(--text-secondary)', fontFamily:"'DM Sans',sans-serif", borderRadius:6 },
  filterTabActive: { background:'#6B1C2A', color:'#fff' },

  primaryBtn: { padding:'8px 16px', borderRadius:7, border:'none', background:'#6B1C2A', color:'#fff', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif" },

  empty: { padding:'40px 10px', textAlign:'center', color:'var(--text-light)', fontSize:13 },

  pillGrid: { display:'flex', flexWrap:'wrap', gap:8 },
  pill: {
    display:'inline-flex', alignItems:'center', gap:8, padding:'6px 10px 6px 12px',
    borderRadius:999, border:'1px solid', background:'#fff',
    fontSize:13, fontWeight:600, color:'var(--text-primary)',
    fontFamily:"'DM Sans',sans-serif",
  },
  rolePill: {
    display:'inline-flex', alignItems:'center', gap:8, padding:'6px 10px 6px 14px',
    borderRadius:999, border:'1px solid var(--border)', background:'#FDFBF8',
    fontSize:13, fontWeight:600, color:'var(--text-primary)',
    fontFamily:"'DM Sans',sans-serif",
  },
  pillDot: { width:10, height:10, borderRadius:'50%', flexShrink:0 },
  pillName: {},
  pillType: { fontSize:10, fontWeight:700, textTransform:'uppercase', letterSpacing:'0.08em', color:'var(--text-light)' },
  pillEdit: {
    width:22, height:22, borderRadius:'50%', border:'none', background:'transparent',
    color:'var(--wine)', cursor:'pointer', padding:0,
    display:'flex', alignItems:'center', justifyContent:'center',
  },
  pillDelete: {
    width:20, height:20, borderRadius:'50%', border:'none', background:'transparent',
    color:'var(--text-light)', cursor:'pointer', fontSize:11, lineHeight:1, padding:0,
    display:'flex', alignItems:'center', justifyContent:'center',
  },

  overlay: { position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, backdropFilter:'blur(2px)' },
  modal: { background:'#fff', borderRadius:16, width:'100%', maxWidth:520, maxHeight:'90vh', overflow:'auto', boxShadow:'0 20px 60px rgba(0,0,0,0.15)' },
  modalHeader: { display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'22px 28px 0', gap:16 },
  modalTitle: { fontFamily:"'Cormorant Garamond',serif", fontSize:22, fontWeight:600, color:'var(--wine)', margin:0 },
  closeBtn: { width:30, height:30, borderRadius:8, border:'1px solid var(--border)', background:'transparent', cursor:'pointer', fontSize:13, color:'var(--text-secondary)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 },

  form: { padding:'18px 28px 26px', display:'flex', flexDirection:'column', gap:14 },
  fieldGroup: { display:'flex', flexDirection:'column', gap:5 },
  fieldLabel: { fontSize:11, fontWeight:600, color:'var(--text-secondary)', letterSpacing:'0.02em' },
  input: { padding:'8px 11px', borderRadius:7, border:'1px solid var(--border)', fontSize:13, fontFamily:"'DM Sans',sans-serif", color:'var(--text-primary)', background:'#FDFBF8', outline:'none', width:'100%', boxSizing:'border-box' },
  modalActions: { display:'flex', justifyContent:'flex-end', gap:10, marginTop:4 },
  cancelBtn: { padding:'8px 18px', borderRadius:7, border:'1px solid var(--border)', background:'transparent', fontSize:13, fontWeight:600, cursor:'pointer', fontFamily:"'DM Sans',sans-serif", color:'var(--text-secondary)' },

  // Mobile-only additions (merged when useBreakpoint().isMobile)
  modalHeaderMobile: { alignItems:'center', padding:'12px 16px 10px', borderBottom:'1px solid var(--border)' },
  formMobile: { padding:'16px 16px 0' },
  modalActionsMobile: { margin:'4px -16px 0', padding:'12px 16px', borderTop:'1px solid var(--border)' },

  // Phase 4 mobile layout
  textMobile: { fontSize:15 },
  cardMobile: { padding:16 },
  cardHeaderMobile: { flexDirection:'column', alignItems:'stretch', gap:12 },
  catControlsMobile: { display:'flex', flexDirection:'column', alignItems:'stretch', gap:10, width:'100%', minWidth:0 },
  filterTabsMobile: { display:'flex', maxWidth:'100%', overflowX:'auto', WebkitOverflowScrolling:'touch', scrollbarWidth:'none', boxSizing:'border-box' },
  filterTabMobile: { ...tap, flex:'1 0 auto', fontSize:14, whiteSpace:'nowrap' },
  pillGridMobile: { flexDirection:'column', flexWrap:'nowrap' },
  pillMobile: { display:'flex', width:'100%', boxSizing:'border-box', fontSize:15, padding:'2px 2px 2px 14px', borderRadius:12, minWidth:0 },
  pillNameMobile: { flex:1, minWidth:0, overflowWrap:'anywhere' },
  pillBtnTouch: { width:44, height:44, flexShrink:0 },
  swatchTouch: { width:44, height:44 },
};
